import { type RefObject, useState } from 'react';
import { Alert, Button, Checkbox } from '@/design-system';
import { COPY } from './copy';
import styles from './Journey.module.css';

export interface LandingStepProps {
  headingRef: RefObject<HTMLHeadingElement | null>;
  sections: number;
  consented: boolean;
  anyComplete: boolean;
  busy: boolean;
  /** Absent when there is nothing to start over: no application yet. */
  onStartOver?: () => void;
  onStart: () => void;
  onContinue: () => void;
}

/**
 * Step 0: what this is, how long it takes, and the privacy notice. The applicant
 * confirms they may share third parties' data before the first section, once.
 */
export function LandingStep({ headingRef, ...props }: LandingStepProps) {
  const [agreed, setAgreed] = useState(props.consented);
  const [confirmingStartOver, setConfirmingStartOver] = useState(false);
  return (
    <section className={styles.card} aria-labelledby="step-heading">
      <h1 id="step-heading" ref={headingRef} tabIndex={-1} className={styles.title}>
        {COPY.landingTitle}
      </h1>
      <p className={styles.intro}>{COPY.landingIntro}</p>
      <p className={styles.intro}>{COPY.landingEstimate(props.sections)}</p>
      <section aria-labelledby="privacy" className={styles.notice}>
        <h2 id="privacy" className={styles.subtitle}>
          {COPY.privacyTitle}
        </h2>
        <p>{COPY.privacyText}</p>
        {!props.consented && (
          <Checkbox label={COPY.consentLabel} checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
        )}
      </section>
      {confirmingStartOver && props.onStartOver && (
        <Alert tone="danger" title={COPY.startOver}>
          {COPY.startOverConfirm}
          <span className={styles.inlineActions}>
            <Button variant="primary" size="sm" disabled={props.busy} onClick={props.onStartOver}>
              {COPY.startOver}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirmingStartOver(false)}>
              Keep my answers
            </Button>
          </span>
        </Alert>
      )}
      <div className={styles.actions}>
        {props.anyComplete ? (
          <>
            <Button disabled={props.busy} onClick={props.onContinue}>
              {COPY.continue}
            </Button>
            {props.onStartOver && (
              <Button variant="secondary" disabled={props.busy} onClick={() => setConfirmingStartOver(true)}>
                {COPY.startOver}
              </Button>
            )}
          </>
        ) : (
          <Button disabled={props.busy || !agreed} onClick={props.onStart}>
            {COPY.start}
          </Button>
        )}
      </div>
      {!agreed && !props.anyComplete && <p className={styles.hint}>{COPY.consentRequired}</p>}
    </section>
  );
}
